import Typography from "@portal/components/atoms/v2/typography";
import { useState } from "react";
import { MainBox } from "../components/mainBox";
import { UserMessageComponent } from "../components/message/UserMessageComponent";
import { RiskInfo, RiskOptions } from "./components";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { ToggleButton } from "@portal/components/atoms/buttons/toggle/ToggleButton";
import { useRobot } from "@portal/app/context/robotProvider";
import { MapRiskAppetiteToIndex, MapIndexToRiskAppetite } from "../components/helpers/MapRiskAppetite";
import { RobotFooterComponent, RobotNavigateToPage } from "../components/footer";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { SavingPeriod } from "@portal/types/robot/IRobotEnums";
import { useSession } from "@portal/auth/client/SessionProvider";

export const RiskPage = ({ centerId, pageIndex, isDefaultLanguage }: IPageProps) => {
  const router = useRouter();
  const { riskLevelRequest: request, setRiskAppetite } = useRobot();
  const [showUserMessage, setShowUserMessage] = useState(false);
  const [toggleBoxOpen, setToggleBoxOpen] = useState(false);
  const isButtonDisabled = request.riskAppetite === null;

  const [shortHorizonUserMessage, setShortHorizonUserMessage] = useState(false);

  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const options = [texts.PAGE_5_A_1, texts.PAGE_5_B_1, texts.PAGE_5_C_1, texts.PAGE_5_D_1];
  const optionsHeaders = [texts.PAGE_5_A, texts.PAGE_5_B, texts.PAGE_5_C, texts.PAGE_5_D];
  const { session } = useSession();
  const isLoggedIn = session?.status === "authenticated";

  const OnSelectionClick = (selectedOption: number) => {
    if (request.savingPeriod === SavingPeriod.ThreeToFive && selectedOption === 3) {
      setShortHorizonUserMessage(true);
    }
    if (selectedOption === 0) {
      setShowUserMessage(true);
      return;
    }
    const riskAppetite = MapIndexToRiskAppetite(selectedOption);
    if (riskAppetite !== null) setRiskAppetite(riskAppetite);
  };

  return (
    <>
      <MainBox>
        <div className="flex mb-1.5">
          <Typography variant="Body">{texts.PAGE_5_INFO_HEADER}</Typography>
        </div>
        <Link prefetch={false} href="https://vff.no/fondshandboken" target="_blank" className="flex underline items-start">
          <Typography variant="Body">{texts.PAGE_5_HEADER_LINK}</Typography>
        </Link>
        <div className="flex mt-2 mb-0.5">
          <Typography variant="H6">{texts.PAGE_5_SUBHEADER}</Typography>
        </div>
        <TextLine />
        <RiskOptions
          optionsHeaders={optionsHeaders}
          optionsTexts={options}
          selectedItem={MapRiskAppetiteToIndex(request.riskAppetite) ?? -1}
          onSelectionClick={OnSelectionClick}
        />
        <ToggleButton
          activeText={texts.PAGE_5_TOGGLE}
          inActiveText={texts.TEXT_CLOSE}
          expanded={toggleBoxOpen}
          setExpanded={setToggleBoxOpen}
        />
        {toggleBoxOpen && <RiskInfo centerId={centerId} isDefaultLanguage={isDefaultLanguage} />}
      </MainBox>

      {showUserMessage && (
        <UserMessageComponent
          userMessage={texts.MESSAGE_NO_FUND_FOR_YOU}
          showDialog={showUserMessage}
          secondaryButtonText={texts.MESSAGE_CHANGE_REPLY}
          secondaryButtonClick={() => {
            setShowUserMessage(false);
          }}
        />
      )}
      {shortHorizonUserMessage && (
        <UserMessageComponent
          content={
            <div>
              <Typography variant="BodySpan">{texts.SHORT_HORIZON_USER_MESSAGE}</Typography>
              <Link href={RobotNavigateToPage(isLoggedIn, 3, centerId)} className="text-blueHighlighted">
                her
              </Link>
            </div>
          }
          showDialog={shortHorizonUserMessage}
          primaryButtonText={texts.SHORT_HORIZON_CONTINUE}
          primaryButtonClick={() => {
            setShortHorizonUserMessage(false);
          }}
          secondaryButtonText={texts.SHORT_HORIZON_GO_BACK}
          secondaryButtonClick={() => {
            setRiskAppetite(null);
            setShortHorizonUserMessage(false);
            router.push(RobotNavigateToPage(isLoggedIn, 4, centerId));
          }}
        />
      )}
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={isButtonDisabled}
      />
    </>
  );
};
